import { CollectionSpaceIcon } from '@tableau/icons/CollectionSpaceIcon';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { Location } from '~commonInfra/utils/Location';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import {
  CompositeSessionInfoSelectors,
  SessionUserSelectors,
} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { callCollectionsReorder } from '~src/spacesPlatform/actions/callCollectionsReorder';
import { clearSpaceContentCountsAction } from '~src/spacesPlatform/actions/ContentCountsActions';
import { showContentTypes } from '~src/spacesPlatform/actions/ContentExplorerFilterActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { getCollectionSpaceContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { selectSelectedContentItems } from '~src/spacesPlatform/selectors/ContentSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo, SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { shareContent } from '../../sharing/actions/ShareContentAction';
import { CollectionsUsageStats } from '../ResourceQuery/CollectionsResourceQuery';
import { CollectionSpaceHeader } from './CollectionSpaceHeader';

export interface CollectionSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly selectedContentItemsCount: number;
  readonly isAdmin: boolean;
  readonly currentUserId: string;
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly allowDragAndDrop: boolean;
}

interface CollectionSpaceDispatchProps {
  readonly shareContent: (content: IContentInfo) => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly callCollectionsReorder: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export interface CollectionSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly reloadSpace: () => void;
  readonly sanitizer: ISanitizer;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export type CollectionSpaceProps = CollectionSpaceStateProps & CollectionSpaceDispatchProps & CollectionSpaceOwnProps;

export const CollectionShareTestId = 'collection-share';
export const CollectionPrivateHeader = 'collection-private-header';

export class CollectionSpace extends React.PureComponent<CollectionSpaceProps> {
  public componentDidMount() {
    if (this.props.spaceInfo.item) {
      CollectionsUsageStats.markViewed(this.props.spaceInfo.item.id);
    }
  }

  private onClickShareButton = (): void => {
    this.props.shareContent(this.props.spaceInfo.item as IContentInfo);
  };

  private onNoContentAction = (): void => {
    Location.instance.setUrl(this.props.urls.exploreUrl());
  };

  public render(): JSX.Element {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={SpaceContainerStyles.MastheadArea + (FeatureFlags.compactHeaderEnabled ? (' ' + SpaceContainerStyles.WithExplorerToolbarInContentSpace) : '')}>
          {this.mastheadElement()}
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={['list', 'thumbnail']}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarContentFilterControl: this.contentFilterControl(),
              toolbarVisibility: FeatureFlags.compactHeaderEnabled?'hidden':'enabled'
            }}
            emptyStateProps={this.emptyStateProps()}
            reorderItemAction={this.props.callCollectionsReorder}
            allowDragAndDrop={this.props.allowDragAndDrop}
            reorderAccessibilityInstructions = {Messages.collectionReorderAccessibilityInstructions()}
          />
        </div>
      </div>
    );
  }

  private contentFilterControl(): JSX.Element {
    const collectionSpaceContentInOrder = getCollectionSpaceContentInOrder();

    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
          <FilterByTypeDropdown
            filterSelected={this.props.filterSelected}
            contentFilter={this.props.contentTypeFilterValues}
            defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
            clearContentCounts={this.props.clearContentCounts}
            spaceType={this.props.spaceInfo.spaceType}
            contentItemDefinitions={collectionSpaceContentInOrder}
          />
      </React.Fragment>
    );
  }

  private emptyStateProps = (): ExplorerEmptyStateProps => {
    const collectionItem = this.props.spaceInfo.item as ISpaceItemDetailInfo<'collection'>;
    if (collectionItem.totalItemCount > 0 && collectionItem.permissionedItemCount === 0) {
      // special empty state if user just doesn't have access to the items in the collection
      return {
        primaryMessage: Messages.collectionSpaceEmtpyStateNoAccessToItemsMessage()
      };
    }

    return {
      icon: CollectionSpaceIcon,
      primaryMessage: Messages.collectionSpaceEmptyStatePrimaryMessage(),
      secondaryMessage: Messages.collectionSpaceEmptyStateSecondaryMessage(),
      onActionButton: this.onNoContentAction,
      actionButtonMessage: Messages.emptyStateCallToAction()
    };
  };

  private mastheadElement(): React.ReactNode {
    const collectionItem = this.props.spaceInfo.item as ISpaceItemDetailInfo<'collection'>;
    return <CollectionSpaceHeader
      item={collectionItem}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      urls={this.props.urls}
      onShare={this.onClickShareButton}
      selectedContentItemsCount={this.props.selectedContentItemsCount}
      isAdminOrOwner={this.props.isAdmin || (this.props.currentUserId === collectionItem.userId)}
      isOwner={this.props.currentUserId === collectionItem.userId}
      sanitizer={this.props.sanitizer}
      reloadSpace={this.props.reloadSpace}
    />;
  }
}

const mapStateToProps: MapStateToProps<CollectionSpaceStateProps> = (state: AppState) => ({
  spaceInfo: selectSpaceInfo(state),
  selectedContentItemsCount: _.values(selectSelectedContentItems(state)).length,
  isAdmin: CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(state),
  currentUserId: SessionUserSelectors.sessionUserId(state),
  contentTypeFilterValues: selectCurrentlyApplicableContentTypes(state),
  defaultContentTypeFilterValues: selectDefaultContentTypeFilterValues(state),
  allowDragAndDrop: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector((state.spaceInfo.item as ISpaceItemDetailInfo<'collection'>).owner)(state)
});

const mapDispatchToProps: MapDispatchToProps<CollectionSpaceDispatchProps> = (dispatch) => bindActionCreators({
  shareContent: shareContent,
  filterSelected: showContentTypes,
  requestSpaceContents: requestSpaceContents,
  clearContentCounts: clearSpaceContentCountsAction,
  callCollectionsReorder: callCollectionsReorder
}, dispatch);

export const ConnectedCollectionSpace = connect(mapStateToProps, mapDispatchToProps)(CollectionSpace);
